import { useEffect, useMemo, useState, useRef, useCallback } from 'react';

import {
  Grid,
  ScrollPanelWithoutScrollbars,
  usePreventWindowScroll,
  useTheme,
} from '@simplywallst/ui-core';
import ActionSheet, {
  Panel,
  Sheet,
  Background,
  Header as ActionSheetHeader,
} from '@/components/ActionSheet';
import { KeyMetric } from '@/components/KeyMetric';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import { NarrativeTabs } from '@/pages/Narrative/components/NarrativeTabs';
import { NarrativeHeader } from '@/pages/Narrative/components/NarrativeHeader';
import type { Narrative } from '@/pages/Narrative/hooks/useNarrative';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';
import { useNarrativeForm } from '@/pages/Narrative/hooks/useNarrativeForm';
import { validateNarrativeForm } from '@/pages/Narrative/utils';

import { ViewValuationButton } from '../../ViewValuationButton';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { NarrativeMainContent } from './NarrativeMainContent';
import { usePastAnnualRevenueEarnings } from '@/pages/Narrative/hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import { ViewEditNarrativeFuturePEMultipleCalculator } from '../../Calculators/ViewEditNarrativeFuturePEMultipleCalculator';
import {
  FuturePEMultipleContext,
  calculateValuationInitialisation,
  useFuturePEMultipleCalculation,
} from '../../Calculators';
import { useNarrativeFormHandler } from '../../NarrativeFormContent';
import { getAuthorFairValueLabel } from '@/pages/Narrative/utils/author';
import { NarrativeSidebar } from '@/pages/Narrative/components/NarrativeSidebar';

type Props = {
  narrative: Narrative;
};

export const NarrativeLayout = ({ narrative }: Props) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    id: narrativeId,
    company,
    valuation,
    userHoldsPosition,
    owner,
  } = narrative;

  if (!company) throw new Error("Narrative's company is missing");

  const currencyIso = getTradingItemCurrencyISO(company.data.currencyInfo);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: valuation?.fiscalData,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        new ViewEditNarrativeFuturePEMultipleCalculator(valuation)
      ),
    [valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  } = useFuturePEMultipleCalculation(strategyContext);

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateValuationInitialisation({
      valuation,
      calculateFairValueFromInitialisation,
      calculateRevenueGrowthRateFromFairValue,
    });

    return {
      ...calculatorOutputs,
      userHoldsPosition: userHoldsPosition ?? false,
      title: '',
      content: {
        text: '',
        html: '',
      },
    };
  }, [
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    userHoldsPosition,
    valuation,
  ]);

  const { values, setValues, setPartialValues } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
  });

  useEffect(() => {
    setValues(initialNarrativeForm);
  }, [narrativeId]);

  const PreventWindowScroll = () => {
    usePreventWindowScroll();
    return null;
  };
  const fairValue = valuation.fairValue;

  const { handleSetFairValue, handleSetPartialValues } =
    useNarrativeFormHandler({
      calculateFairValue,
      calculateRevenueGrowthRateFromFairValue,
      setPartialValues,
      values,
    });

  const breakdown = calculateBreakDown({
    discountRatePercentage: values.discountRatePercentage,
    futurePE: values.priceEarningsMultiple,
    profitMarginPercentage: values.profitMarginPercentage,
    revenueGrowthRatePercentage: values.revenuePercentage,
    timeFrame: values.timePeriod,
  });

  const authorFairValueLabel = getAuthorFairValueLabel({
    displayName: owner?.displayName,
    narrativeType: narrative.type,
  });

  const commentRef = useRef<HTMLDivElement>(null);

  const handleCommentClick = useCallback(() => {
    commentRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="grid-cols-[1fr_375px] gap-4 lg:grid">
      <div className="lg:grid-rows-[minmax(525px,650px)_2fr]">
        <div className="grid gap-x2">
          {company && (
            <NarrativeHeader
              narrative={narrative}
              onCommentClick={handleCommentClick}
            />
          )}
          <div className="grid gap-x2">
            <div className="grid rounded-none bg-surface-1 p-x2 lg:hidden lg:rounded-md">
              <div className="grid gap-x2">
                <div
                  className="grid h-x4 grid-cols-[auto_auto] items-center justify-between"
                  ref={containerRef}
                >
                  <p className="m-x0 font-medium text-soft">
                    Author&apos;s Valuation
                  </p>
                </div>
                <KeyMetric>
                  <div className="grid grid-cols-[min-content_auto] gap-x2">
                    <p className="m-x0 text-mega font-medium text-solid">
                      {typeof fairValue === 'number'
                        ? toCurrencyTruncated(
                            fairValue,
                            1,
                            currencyIso ?? 'XXX'
                          )
                        : 'N/A'}
                    </p>
                    <CompanyIntrinsicDiscount
                      lastSharePrice={
                        company.analysisValue?.lastSharePrice ?? null
                      }
                      npvPerShare={company.analysisValue?.npvPerShare ?? null}
                      fairValue={fairValue}
                    />
                  </div>
                  <p className="m-x0 text-xs text-soft">
                    {authorFairValueLabel}
                  </p>
                </KeyMetric>
                <ViewValuationButton onModalOpen={() => setIsModalOpen(true)} />
                <ActionSheet
                  open={isModalOpen}
                  onClose={() => {
                    setIsModalOpen(false);
                    setValues(initialNarrativeForm);
                  }}
                >
                  {isModalOpen && <PreventWindowScroll />}
                  <Background
                    onClick={() => {
                      setIsModalOpen(false);
                      setValues(initialNarrativeForm);
                    }}
                  />
                  <Sheet>
                    <Grid
                      templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}
                    >
                      <Panel templateRows="max-content max-content" row="2">
                        <ActionSheetHeader />
                        <ScrollPanelWithoutScrollbars overflow="auto">
                          <NarrativeTabs
                            company={company}
                            values={values}
                            onChange={handleSetPartialValues}
                            onSetFairValue={handleSetFairValue}
                            readOnly
                            owner={{
                              displayName: owner.displayName || '',
                              publicId: owner.publicId,
                            }}
                            narrativeType={narrative.type}
                            onReset={() => setValues(initialNarrativeForm)}
                            pastAnnualRevenueEarnings={
                              pastAnnualRevenueEarnings
                            }
                            calculationBreakDownOutput={breakdown}
                            fixedHeight
                            initialFairValue={initialNarrativeForm.fairValue}
                          />
                        </ScrollPanelWithoutScrollbars>
                      </Panel>
                    </Grid>
                  </Sheet>
                </ActionSheet>
              </div>
            </div>
            <NarrativeMainContent
              narrative={narrative}
              commentRef={commentRef}
            />
          </div>
        </div>
      </div>
      <div className="sticky top-20 z-20 hidden self-start lg:block">
        <NarrativeSidebar
          company={company}
          values={values}
          onChange={handleSetPartialValues}
          onSetFairValue={handleSetFairValue}
          readOnly
          owner={{
            displayName: owner.displayName || '',
            publicId: owner.publicId,
          }}
          narrativeType={narrative.type}
          onReset={() => setValues(initialNarrativeForm)}
          pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
          narrativeId={narrativeId}
          calculationBreakDownOutput={breakdown}
          initialFairValue={initialNarrativeForm.fairValue}
        />
      </div>
    </div>
  );
};
